import React, { useMemo } from 'react'
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    MenuItem,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
} from '@material-ui/icons'

import { useAuditResults } from '../../hooks/useAuditResults'
import { useLeaseAuditDetail } from '../../hooks/useLeaseAuditDetail'
import { AnalysisSummary, TermStatusFilter } from '../../types'
import { useAnalysisRootStyles } from './styles'

interface AnalysisTabProps {
    auditId: number
}

export const AnalysisRoot: React.FC<AnalysisTabProps> = ({ auditId }) => {
    const classes = useAnalysisRootStyles()

    // Get lease audit data for global term status filter
    const { leaseAudit, updateLeaseAudit } = useLeaseAuditDetail({ auditId })
    const termStatusFilter = leaseAudit?.term_status_filter || 'CURRENT_ONLY'

    // Helper functions to map between backend format and component format
    const mapFilterToLocal = (
        filter: TermStatusFilter,
    ): 'all' | 'current' | 'current_future' => {
        switch (filter) {
            case 'ALL_PERIODS':
                return 'all'
            case 'CURRENT_ONLY':
                return 'current'
            case 'CURRENT_AND_FUTURE':
                return 'current_future'
        }
    }

    const mapFilterToBackend = (
        filter: 'all' | 'current' | 'current_future',
    ): TermStatusFilter => {
        switch (filter) {
            case 'all':
                return 'ALL_PERIODS'
            case 'current':
                return 'CURRENT_ONLY'
            case 'current_future':
                return 'CURRENT_AND_FUTURE'
        }
    }

    const termStatusFilterLocal = leaseAudit?.term_status_filter
        ? mapFilterToLocal(leaseAudit.term_status_filter)
        : 'current'

    // Fetch audit results
    const { attributes, residentTerms, isLoading, error } = useAuditResults({
        auditId,
        termStatusFilter,
    })

    // Calculate analysis summaries from audit data
    const analysisSummaries = useMemo((): AnalysisSummary[] => {
        if (!attributes || !residentTerms) {
            return []
        }

        // Filter for currency attributes that should be extracted
        const currencyAttributes = attributes.filter(
            (attr) => attr.data_type === 'CURRENCY' && attr.should_extract,
        )

        // Calculate totals for each currency attribute
        return currencyAttributes.map((attr) => {
            let rentRollTotal = 0
            let documentTotal = 0

            // Sum values across all resident terms
            residentTerms.forEach((term) => {
                const attrValue = term.attribute_values.find(
                    (av) => av.attribute_id === attr.id,
                )

                if (attrValue) {
                    // Parse rent roll value
                    const rrValue = parseFloat(attrValue.rent_roll_value || '0')
                    if (!isNaN(rrValue)) {
                        rentRollTotal += rrValue
                    }

                    // Parse document value
                    const docValue = parseFloat(attrValue.document_value || '0')
                    if (!isNaN(docValue)) {
                        documentTotal += docValue
                    }
                }
            })

            // Calculate variance
            const variance = rentRollTotal - documentTotal
            const variancePercentage =
                rentRollTotal !== 0 ? (variance / rentRollTotal) * 100 : 0

            return {
                attribute_name: attr.name,
                rent_roll_total: rentRollTotal,
                document_total: documentTotal,
                variance: variance,
                variance_percentage: variancePercentage,
            }
        })
    }, [attributes, residentTerms])

    // Calculate totals from analysis summaries
    const totals = analysisSummaries.reduce(
        (acc, item) => ({
            rentRoll: acc.rentRoll + item.rent_roll_total,
            document: acc.document + item.document_total,
            variance: acc.variance + Math.abs(item.variance),
        }),
        { rentRoll: 0, document: 0, variance: 0 },
    )

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value)
    }

    const formatPercentage = (value: number) => {
        const sign = value > 0 ? '+' : ''
        return `${sign}${value.toFixed(2)}%`
    }

    const getVarianceClass = (variance: number) => {
        if (variance > 0) return classes.variancePositive
        if (variance < 0) return classes.varianceNegative
        return classes.varianceZero
    }

    const getVarianceIcon = (variance: number) => {
        if (variance > 0)
            return <TrendingUpIcon className={classes.varianceIcon} />
        if (variance < 0)
            return <TrendingDownIcon className={classes.varianceIcon} />
        return <TrendingFlatIcon className={classes.varianceIcon} />
    }

    if (isLoading) {
        return (
            <Box className={classes.loadingState}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {/* Header */}
            <Box className={classes.header}>
                <Typography className={classes.title}>
                    Variance Analysis
                </Typography>
                <Typography className={classes.subtitle}>
                    Compare rent roll totals with extracted lease document
                    values
                </Typography>
                {/* Term Status Filter */}
                <Box style={{ marginTop: 16 }}>
                    <TextField
                        select
                        label="Period"
                        value={termStatusFilterLocal}
                        onChange={(e) => {
                            const newValue = e.target.value as
                                | 'all'
                                | 'current'
                                | 'current_future'
                            updateLeaseAudit({
                                term_status_filter: mapFilterToBackend(
                                    newValue,
                                ),
                            })
                        }}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 180 }}
                    >
                        <MenuItem value="all">All Periods</MenuItem>
                        <MenuItem value="current">Current Only</MenuItem>
                        <MenuItem value="current_future">
                            Current + Future
                        </MenuItem>
                    </TextField>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Box className={classes.summaryCards}>
                <Card className={classes.summaryCard} elevation={0}>
                    <CardContent className={classes.summaryCardContent}>
                        <Typography className={classes.summaryValue}>
                            {formatCurrency(totals.rentRoll)}
                        </Typography>
                        <Typography className={classes.summaryLabel}>
                            Total Rent Roll Value
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.summaryCard} elevation={0}>
                    <CardContent className={classes.summaryCardContent}>
                        <Typography className={classes.summaryValue}>
                            {formatCurrency(totals.document)}
                        </Typography>
                        <Typography className={classes.summaryLabel}>
                            Total Document Value
                        </Typography>
                    </CardContent>
                </Card>
                <Card className={classes.summaryCard} elevation={0}>
                    <CardContent className={classes.summaryCardContent}>
                        <Typography
                            className={classes.summaryValue}
                            style={{ color: '#ef4444' }}
                        >
                            {formatCurrency(totals.variance)}
                        </Typography>
                        <Typography className={classes.summaryLabel}>
                            Total Variance (Absolute)
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Variance Table */}
            <TableContainer
                component={Paper}
                className={classes.tableContainer}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headerCell}>
                                Attribute
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="right"
                            >
                                Rent Roll Total
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="right"
                            >
                                Document Total
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="right"
                            >
                                Variance
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="right"
                            >
                                Variance %
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {analysisSummaries.map((row) => (
                            <TableRow key={row.attribute_name} hover>
                                <TableCell>
                                    <Typography style={{ fontWeight: 600 }}>
                                        {row.attribute_name}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={classes.currencyCell}
                                >
                                    {formatCurrency(row.rent_roll_total)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={classes.currencyCell}
                                >
                                    {formatCurrency(row.document_total)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={`${
                                        classes.currencyCell
                                    } ${getVarianceClass(row.variance)}`}
                                >
                                    {getVarianceIcon(row.variance)}
                                    {formatCurrency(Math.abs(row.variance))}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={getVarianceClass(
                                        row.variance_percentage,
                                    )}
                                >
                                    {formatPercentage(row.variance_percentage)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Empty State */}
            {analysisSummaries.length === 0 && !isLoading && (
                <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                        No variance data available
                    </Typography>
                    <Typography variant="body2">
                        Complete the audit process to see variance analysis
                    </Typography>
                </Box>
            )}
        </Box>
    )
}
